/**
 * @description 对文件树的数据做权限/验证等相关处理，包含但不限于不允许添加/修改内部数据
 */
import { translateLang } from '@/data/i18n/translateLang'

import {
  CLASSIFY_SUPPORT_MAP,
  UNCLASSIFIABLE_TYPES,
  categoryOptions,
} from '../data'
import { File, FileType, Folder } from '../types'

type ClassifySupportKey = keyof typeof CLASSIFY_SUPPORT_MAP

/** @description 分类名称映射 */
const CLASSIFY_NAME_MAP = categoryOptions.reduce<Record<string, string>>(
  (acc, item) => {
    acc[item.value] = item.name
    return acc
  },
  {}
)
/** @description 分类括号正则 */
const CLASSIFY_BRACKET_REG = /[{}]/g

/**
 * @description 获取分类键
 * @param chain 分类链
 * @returns 分类键
 */
function getClassifyKey(chain: string) {
  return chain.replace(CLASSIFY_BRACKET_REG, '') as ClassifySupportKey
}

/**
 * @description 获取分类名称
 * @param chain 分类链
 * @returns 分类名称
 */
function getClassifyName(chain: string) {
  return CLASSIFY_NAME_MAP[chain] || chain
}

/**
 * @description 判断文件类型是否能被指定分类链所分类
 * @param fileType 文件类型
 * @param classifyChains 分类链
 */
export function isFileTypeClassifiable(
  fileType: FileType,
  classifyChains: string[]
): boolean {
  if (!classifyChains.length) return true
  if (UNCLASSIFIABLE_TYPES.has(fileType)) return false
  return classifyChains.every((chain) => {
    const classifyKey = getClassifyKey(chain)
    const supportedTypes = CLASSIFY_SUPPORT_MAP[
      classifyKey
    ] as readonly string[]
    if (!supportedTypes) return true
    return supportedTypes.includes(fileType)
  })
}

/**
 * @description 构建文件分类错误信息
 * @param file 文件
 * @param classifyChains 分类链
 * @returns 文件分类错误信息
 */
function buildFileErrorMsgs(file: File, classifyChains: string[]) {
  if (!classifyChains.length) return []
  const invalidClassifyNames: string[] = []
  const nameSet = new Set<string>()
  const isUnclassifiable = UNCLASSIFIABLE_TYPES.has(file.type)
  classifyChains.forEach((chain) => {
    const classifyName = getClassifyName(chain)
    if (nameSet.has(classifyName)) return
    if (isUnclassifiable) {
      nameSet.add(classifyName)
      invalidClassifyNames.push(classifyName)
      return
    }
    const classifyKey = getClassifyKey(chain)
    const supportedTypes = CLASSIFY_SUPPORT_MAP[
      classifyKey
    ] as readonly File['type'][]
    if (!supportedTypes) return
    // 检查文件类型是否明确规定过支持
    if (!supportedTypes.includes(file.type)) {
      nameSet.add(classifyName)
      invalidClassifyNames.push(classifyName)
    }
  })
  if (!invalidClassifyNames.length) return []
  return [
    isUnclassifiable
      ? translateLang('该类型文件不能放在任何分类文件夹下')
      : translateLang('此文件不能按照{classifyNames}分类', {
          classifyNames: invalidClassifyNames.join('、'),
        }),
  ]
}

/** @description 文件夹权限处理类 */
export class FolderAuthority {
  /** @description 校验文件夹树内文件分类权限 */
  static validateFolderTree(rootFolder: Folder) {
    this.validateFolder(rootFolder, [])
  }

  /** @description 递归校验文件夹及其子文件 */
  private static validateFolder(
    folder: Folder,
    parentClassifyChains: string[]
  ) {
    const currentClassifyChains = [
      ...parentClassifyChains,
      ...folder.accept.classifyChains,
    ]
    if (folder.accept.supportedFiles?.length) {
      folder.accept.supportedFiles.forEach((file) => {
        const errorMsgs = buildFileErrorMsgs(file, currentClassifyChains)
        file.validateErrorMsgs = errorMsgs.length ? errorMsgs : undefined
      })
    }
    if (folder.children?.length) {
      folder.children.forEach((child) => {
        this.validateFolder(child, currentClassifyChains)
      })
    }
  }
}
